import { createAsyncThunk } from "@reduxjs/toolkit";

import type { PollService } from "@busbud/core-types";
import type { PublicTripLeg } from "@busbud/core-types/dist/lib/poll-service/api/udm";

import type { ApplicationContext } from "@app/context/app-context";
import { toMaybeApiError } from "@app/helpers/api-error";
import { isInterconnection } from "@app/helpers/udm";
import type { ResultsReduxState } from "@app/types/results-redux-types";

import { getTripById } from "../../selectors/trips/get-trip";

export interface GetTripDetailsPayload {
  api: ApplicationContext["api"];
  trip_id: string;
}

interface ThunkConfig {
  state: ResultsReduxState;
  rejectValue: Error;
}

type SegmentsVehicle = Record<string, PollService.API.GetVehicleResponse>;

export const getTripDetailsThunk = createAsyncThunk<
  SegmentsVehicle,
  GetTripDetailsPayload,
  ThunkConfig
>(
  "results/getTripDetails",
  async ({ trip_id, api }, { getState }): Promise<SegmentsVehicle> => {
    try {
      const state = getState();
      const trip = getTripById(state, trip_id);
      // @ts-expect-error TS18048 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
      const segments_ids = trip.segment_ids;

      const is_outbound_interconnection = isInterconnection(
        trip,
        // @ts-expect-error TS18048 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
        trip.trip_legs[0]
      );

      // In case of a oneway, the second leg will be undefined
      // @ts-expect-error TS18048 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
      const return_leg: PublicTripLeg | undefined = trip.trip_legs[1];

      const is_return_interconnection =
        // @ts-expect-error TS18048 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
        trip.trip_legs.length > 1 ? isInterconnection(trip, return_leg) : false;

      // In case of interconnection, we rather use the getTripDetails endpoint to fetch the vehicle layout
      // because the GET /vehicle endpoint does not currently support interconnections
      if (is_outbound_interconnection || is_return_interconnection) {
        const response = await api.getTripDetailsRelated(trip_id);
        return segments_ids.reduce<SegmentsVehicle>(
          (segments_vehicle, segment_id) => {
            // @ts-expect-error TS2322 Unsafe indexed access (noUncheckedIndexedAccess); tracked in #24591
            segments_vehicle[segment_id] =
              response.related.segments[segment_id];
            return segments_vehicle;
          },
          {}
        );
      }

      const all_segments = await Promise.all(
        segments_ids.map(s_id =>
          api
            .getTripSegmentVehicle(trip_id, s_id)
            .then(res => ({ segment_id: s_id, vehicle: res }))
        )
      );

      return all_segments.reduce<SegmentsVehicle>(
        (segments_vehicle, segment) => {
          segments_vehicle[segment.segment_id] = segment.vehicle;
          return segments_vehicle;
        },
        {}
      );
    } catch (err) {
      throw toMaybeApiError(err);
    }
  }
);
